import { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, Chip, Alert } from '@mui/material';
import { Assignment as TemplateIcon } from '@mui/icons-material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomCheckbox,
  LoadingSkeleton,
} from '../../../../common/components';
import { useAssignmentsByProject } from '../../../../hooks/useProjectTaskAssignments';

/**
 * Modal para asignar templates a proyectos
 * Siguiendo los patrones del proyecto
 */
const AssignTemplatesDialog = ({
  open,
  onClose,
  project,
  taskTemplates,
  onAssign,
}) => {
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  const { data: currentAssignments = [], isLoading } = useAssignmentsByProject(
    project?.uuid, 
    { enabled: open && !!project }
  );

  useEffect(() => {
    if (currentAssignments && open) {
      const currentTemplateIds = currentAssignments.map(assignment => assignment.uuid_task_template);
      setSelectedTemplates(currentTemplateIds);
      setHasChanges(false);
    }
  }, [currentAssignments, open]);

  const handleTemplateToggle = (templateId) => {
    setSelectedTemplates(prev => {
      const newSelection = prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId];
      
      const currentTemplateIds = currentAssignments.map(assignment => assignment.uuid_task_template);
      setHasChanges(JSON.stringify(newSelection.sort()) !== JSON.stringify(currentTemplateIds.sort()));
      
      return newSelection;
    });
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    
    try {
      await onAssign(project, selectedTemplates);
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Error al asignar templates:', error);
    }
  };

  const handleClose = () => {
    setHasChanges(false);
    setSelectedTemplates([]);
    onClose();
  };

  // Agrupar templates por tipo
  const groupedTemplates = taskTemplates.reduce((acc, template) => {
    if (!acc[template.template_type]) {
      acc[template.template_type] = [];
    }
    acc[template.template_type].push(template);
    return acc;
  }, {});

  const templateTypeLabels = {
    progress: 'Templates de Progreso',
    quality: 'Templates de Calidad',
  };

  return (
    <DialogContainer open={open} onClose={handleClose} size="medium">
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <TemplateIcon color="primary" />
          Asignar Templates - {project?.name}
        </Box>
      </DialogTitle>

      <DialogBody>
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <Box>
            {hasChanges && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Tienes cambios sin guardar en la asignación de templates.
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Selecciona los templates que deseas asignar a este proyecto. 
              Los templates asignados generarán automáticamente las tareas de seguimiento correspondientes.
            </Typography>

            {Object.entries(groupedTemplates).map(([type, templates]) => (
              <Card key={type} sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {templateTypeLabels[type] || `Templates ${type}`}
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {templates.map(template => {
                      const isSelected = selectedTemplates.includes(template.uuid);
                      const isCurrentlyAssigned = currentAssignments.some(
                        assignment => assignment.uuid_task_template === template.uuid
                      );
                      
                      return (
                        <Grid item xs={12} sm={6} key={template.uuid}>
                          <Card 
                            variant="outlined"
                            sx={{ 
                              cursor: 'pointer',
                              bgcolor: isSelected ? 'primary.50' : 'inherit',
                              border: isSelected ? '2px solid' : '1px solid',
                              borderColor: isSelected ? 'primary.main' : 'divider',
                              '&:hover': {
                                bgcolor: isSelected ? 'primary.100' : 'grey.50',
                              }
                            }}
                            onClick={() => handleTemplateToggle(template.uuid)}
                          >
                            <CardContent sx={{ py: 2 }}>
                              <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                  <Typography variant="subtitle2" fontWeight="medium">
                                    {template.name}
                                  </Typography>
                                  {template.description && (
                                    <Typography variant="caption" color="text.secondary">
                                      {template.description}
                                    </Typography>
                                  )}
                                </Box>
                                
                                <Box display="flex" alignItems="center" gap={1}>
                                  {isCurrentlyAssigned && !isSelected && (
                                    <Chip 
                                      label="Se eliminará" 
                                      color="warning" 
                                      size="small" 
                                    />
                                  )}
                                  {!isCurrentlyAssigned && isSelected && (
                                    <Chip 
                                      label="Se agregará" 
                                      color="success" 
                                      size="small" 
                                    />
                                  )}
                                  <CustomCheckbox
                                    checked={isSelected}
                                    onChange={() => handleTemplateToggle(template.uuid)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            ))}

            {taskTemplates.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                No hay templates disponibles para asignar.
              </Typography>
            )}
          </Box>
        )}
      </DialogBody>

      <DialogActions 
        handleClose={handleClose}
        handleSave={handleSave}
        saveDisabled={!hasChanges}
        saveText="Asignar Templates"
      />
    </DialogContainer>
  );
};

export default AssignTemplatesDialog;